import React, { useState, useEffect } from 'react';
import { communityAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { PawPrint, CheckCircle2, Eye, FileText } from 'lucide-react';

import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function PetsTab({ communityUsers }) {
  const [petsData, setPetsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPets();
  }, [communityUsers]);

  const loadPets = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      // Fetch profiles for all residents to get their pets
      const response = await fetch(`${API_URL}/community/pets-overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPetsData(data);
      }
    } catch (error) {
      console.error('Failed to load pets data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (userId, petId) => {
    try {
      await communityAPI.verifyPetVaccination(userId, petId);
      toast.success('Pet vaccination verified');
      loadPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    }
  };

  const handleViewDoc = async (userId, petId) => {
    try {
      const token = getAuthToken();
      const url = `${API_URL}/community/pets/${userId}/${petId}/vaccination-doc`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('No document');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('No vaccination document uploaded yet');
    }
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PawPrint className="w-5 h-5 text-orange-600" />
          Pet Vaccination Management
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center text-slate-500 py-8">Loading...</p>
        ) : petsData.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No pets registered in the community</p>
        ) : (
          <div className="space-y-3">
            {petsData.map((entry) => (
              <div key={`${entry.user_id}-${entry.pet.id}`} data-testid={`mgmt-pet-${entry.pet.id}`} className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center">
                      <PawPrint className="w-5 h-5 text-orange-700" />
                    </div>
                    <div>
                      <p className="font-semibold">{entry.pet.name} <Badge variant="outline" className="text-xs capitalize ml-1">{entry.pet.pet_type}</Badge></p>
                      <p className="text-xs text-slate-500">{entry.pet.breed || 'Breed not specified'}</p>
                      <p className="text-xs text-slate-600 mt-0.5">Owner: <span className="font-medium">{entry.user_name}</span> — Villa {entry.villa_number}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-[10px] ${entry.pet.vaccination_status === 'verified' ? 'bg-emerald-600' : 'bg-amber-500'}`}>
                          {entry.pet.vaccination_status === 'verified' ? 'Verified' : 'Pending Verification'}
                        </Badge>
                        {entry.pet.vaccination_document && (
                          <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600">
                            <FileText className="w-3 h-3 mr-0.5" /> Doc Uploaded
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {entry.pet.vaccination_document && (
                      <Button
                        data-testid={`mgmt-view-doc-${entry.pet.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDoc(entry.user_id, entry.pet.id)}
                        className="text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View Doc
                      </Button>
                    )}
                    {entry.pet.vaccination_status !== 'verified' && (
                      <Button
                        data-testid={`mgmt-verify-pet-${entry.pet.id}`}
                        size="sm"
                        onClick={() => handleVerify(entry.user_id, entry.pet.id)}
                        className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Verify
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
